import * as Sentry from '@sentry/react';
import { type ToastT, toast } from 'sonner';
import { ZodError, z } from 'zod';
import { fromZodError } from 'zod-validation-error/v3';
import { deepCopy } from '@/utils/deepCopy';

const nestErrorEnvelopeSchema = z.object({
  message: z.union([z.string(), z.array(z.string())]).optional(),
  error: z.string().optional(),
  statusCode: z.number().optional()
});

type Options = Partial<
  {
    customMessage: string;
    errorType: 'error' | 'warning';
    isConsoleLogged: boolean;
    sentryDisabled: boolean;
  } & ToastT
>;

const defaultOptions: Options = {
  errorType: 'error',
  isConsoleLogged: true
};

export const newError = (
  uniqueErrorCode: `${string}-${string}`,
  error: unknown,
  /** withToast */
  withToast = false,
  options?: Options
): Error => {
  const finalOptions: Options = {
    ...defaultOptions,
    title: `Error ${uniqueErrorCode}`,
    description: 'An unexpected error has occurred',
    ...options
  };
  Sentry.setExtra('studio_error_code', uniqueErrorCode);

  if (finalOptions.isConsoleLogged) {
    toBrowserConsole(error, finalOptions.errorType);
  }

  if (typeof error === 'string') {
    const finalError = new Error(error);
    if (withToast) {
      renderNewToast(error, finalOptions);
    }
    if (!finalOptions.sentryDisabled) {
      Sentry.captureException(finalError);
    }
    return finalError;
  }

  if (isArrayOfStrings(error)) {
    const finalError = new Error(error.join('\n'));
    error.forEach(errorMessage => {
      if (withToast) {
        renderNewToast(errorMessage, finalOptions);
      }
    });
    if (!finalOptions.sentryDisabled) {
      Sentry.captureException(finalError);
    }
    return finalError;
  }

  return handleUnknownError(error, withToast, finalOptions);
};

const handleUnknownError = (
  error: unknown,
  withToast: boolean,
  options: Options
): Error => {
  if (!error) {
    //? this will probably not happen but just in case
    const undefinedError = new Error(
      'Undefined error found in handleUnknownError'
    );
    Sentry.captureException(undefinedError);
    throw undefinedError;
  }

  let message = 'Unknown error';

  if (isZodError(error)) {
    // TODO: temporary fix waiting for real migration to zod 4
    try {
      message = fromZodError(error).message;
    } catch (fromZodErrorError) {
      console.error(
        `Error catched from fromZodError method: ${fromZodErrorError}`
      );
      message = 'Unable to create a proper Zod error message';
    }
  } else if (error instanceof Error || isMessageInError(error)) {
    message = error.message;
  }

  if (withToast) {
    renderNewToast(message, options);
  }

  if (!options.sentryDisabled) {
    Sentry.captureException(error);
  }

  return error instanceof Error ? error : new Error(message);
};

const isZodError = (error: unknown): error is ZodError =>
  error instanceof ZodError;

const isArrayOfStrings = (error: unknown): error is string[] =>
  Array.isArray(error) && error.every(e => typeof e === 'string');

const isMessageInError = (error: unknown): error is { message: string } =>
  !!error &&
  typeof error === 'object' &&
  'message' in error &&
  typeof error.message === 'string';

const renderNewToast = (message: string, options: Options): void => {
  const toastMessage = options?.customMessage ?? message;
  const toastOptions = deepCopy(options);
  delete toastOptions.customMessage;
  delete toastOptions.errorType;
  delete toastOptions.isConsoleLogged;
  options.description = toastMessage;
  switch (options?.errorType) {
    case 'warning':
      toast.warning(toastMessage, options);
      break;
    default: // eslint-disable-next-line no-restricted-syntax
      toast.error(toastMessage, options);
  }
};

const toBrowserConsole = (error: unknown, type: Options['errorType']): void => {
  switch (type) {
    case 'warning':
      console.warn(error);
      break;
    default: // eslint-disable-next-line no-restricted-syntax
      console.error(error);
  }
};

/**
 * Extract a human-readable message from an error.
 * Handles NestJS JSON error format: { message, error, statusCode }
 */
export const extractErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'An unexpected error occurred';
  }
  try {
    const rawParsed: unknown = JSON.parse(error.message);
    const result = nestErrorEnvelopeSchema.safeParse(rawParsed);
    if (!result.success) {
      // Parsed JSON didn't match the expected envelope shape; fall through
      // to the raw-message handling below.
      throw new Error('Invalid Nest error envelope');
    }
    const parsed = result.data;
    if (Array.isArray(parsed.message) && parsed.message.length > 0) {
      return parsed.message[0];
    }
    if (typeof parsed.message === 'string' && parsed.message.length > 0) {
      return parsed.message;
    }
    // Fallback to the "error" field (e.g., "Bad Request", "Internal Server Error")
    if (typeof parsed.error === 'string') {
      return parsed.error;
    }
    // Empty JSON body (e.g., {}) or empty message array — service likely unreachable
    if (parsed.statusCode !== undefined) {
      return `Server error (${String(parsed.statusCode)})`;
    }
  } catch {
    // not JSON or invalid envelope, use raw message
  }
  // If message is just "{}" or empty, provide a fallback
  if (error.message === '{}' || error.message.trim() === '') {
    return 'An unexpected server error occurred.';
  }
  return error.message;
};
